import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Shield, 
  TrendingUp, 
  BarChart3, 
  Globe, 
  Zap, 
  Building2,
  Ship,
  Wheat,
  Landmark,
  ChevronRight,
  ArrowRight,
  CheckCircle,
  Brain,
  Database,
  LineChart,
  Menu,
  X,
  ChevronLeft,
  FileSearch,
  PieChart,
  RefreshCcw,
  Play,
  Eye,
} from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_creditai-africa/artifacts/q9fyvy5m_ChatGPT%20Image%20Feb%201%2C%202026%2C%2012_34_17%20PM.png";
const HERO_IMAGE = "https://images.unsplash.com/photo-1581006867287-9f90a3f3a8b0?w=1920&q=80";

// Partner logos with actual uploaded images
const PARTNERS = [
  { 
    name: 'AfDB', 
    fullName: 'African Development Bank',
    logo: 'https://customer-assets.emergentagent.com/job_apaka-credit/artifacts/qgxmrfvn_AfDB-logo.webp'
  },
  { 
    name: 'Afreximbank', 
    fullName: 'African Export-Import Bank',
    logo: 'https://customer-assets.emergentagent.com/job_apaka-credit/artifacts/doht9xzi_Afreximbank_Logo_Secondary_CMYK.jpg'
  },
  { 
    name: 'AFC', 
    fullName: 'Africa Finance Corporation',
    logo: 'https://customer-assets.emergentagent.com/job_apaka-credit/artifacts/ftdt7b4t_AFC.png'
  },
  { 
    name: 'IFC', 
    fullName: 'International Finance Corporation',
    logo: 'https://customer-assets.emergentagent.com/job_apaka-credit/artifacts/vm9ew7kb_IFC-Logo.svg'
  },
  { 
    name: 'KfW DEG', 
    fullName: 'KfW DEG',
    logo: 'https://customer-assets.emergentagent.com/job_apaka-credit/artifacts/gh96hmmh_kwf%20deg.png'
  },
  { 
    name: 'TDB', 
    fullName: 'Trade & Development Bank',
    logo: 'https://customer-assets.emergentagent.com/job_smartrisk-3/artifacts/hgehxvft_TDB.jpg'
  },
  { 
    name: 'FMO', 
    fullName: 'Dutch Development Bank',
    logo: 'https://customer-assets.emergentagent.com/job_smartrisk-3/artifacts/x2iirboe_FMO.jpg'
  },
  { 
    name: 'Proparco', 
    fullName: 'Proparco',
    logo: 'https://customer-assets.emergentagent.com/job_smartrisk-3/artifacts/n9ahrfkc_Proparco.png'
  },
];

export default function LandingPage() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [currentPartnerIndex, setCurrentPartnerIndex] = useState(0);

  // Auto-scroll partners
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentPartnerIndex((prev) => (prev + 1) % PARTNERS.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              <img src={LOGO_URL} alt="Apaka" className="h-20 w-auto object-contain" data-testid="logo" />
            </div>
            
            {/* Desktop Nav */}
            <div className="hidden md:flex items-center gap-8">
              <a href="#products" className="text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors">Products</a>
              <a href="#features" className="text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors">Features</a>
              <a href="#sectors" className="text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors">Sectors</a>
              <a href="#how-it-works" className="text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors">How It Works</a>
              <Link to="/login">
                <Button variant="outline" className="border-slate-300" data-testid="nav-login-btn">
                  Sign In
                </Button>
              </Link>
            </div>

            {/* Mobile menu button */}
            <button 
              className="md:hidden p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-slate-200 py-4">
            <div className="flex flex-col gap-4 px-4">
              <a href="#products" className="text-sm font-medium text-slate-600">Products</a>
              <a href="#features" className="text-sm font-medium text-slate-600">Features</a>
              <a href="#sectors" className="text-sm font-medium text-slate-600">Sectors</a>
              <a href="#how-it-works" className="text-sm font-medium text-slate-600">How It Works</a>
              <Link to="/login">
                <Button variant="outline" className="w-full">Sign In</Button>
              </Link>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section with Image Background */}
      <section className="relative min-h-[90vh] flex items-center pt-16">
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <img 
            src={HERO_IMAGE} 
            alt="African Cityscape" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/95 via-slate-900/80 to-slate-900/60" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div className="text-white">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full mb-6 animate-fade-in-up">
                <Zap className="h-4 w-4 text-amber-400" />
                <span className="text-sm font-medium">AI-Powered Credit Intelligence</span>
              </div>
              
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black leading-tight mb-6 animate-fade-in-up delay-100" data-testid="hero-title">
                Credit & Risk Infrastructure for{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-amber-400">
                  Frontier Markets
                </span>
              </h1>
              
              <p className="text-lg md:text-xl text-slate-300 mb-8 max-w-xl animate-fade-in-up delay-200">
                The AI-native underwriting engine purpose-built for Africa and emerging markets. 
                Predict risk. Price dynamically. Make decisions with confidence.
              </p>

              {/* Stats Row */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 animate-fade-in-up delay-300">
                {[
                  { value: '12+', label: 'Markets' },
                  { value: '$2B+', label: 'Analyzed' },
                  { value: '85%', label: 'Accuracy' },
                  { value: '40%', label: 'Faster' },
                ].map((stat, i) => (
                  <div key={i} className="text-center p-3 bg-white/5 backdrop-blur-sm rounded-lg border border-white/10">
                    <div className="text-2xl font-heading font-black text-white">{stat.value}</div>
                    <div className="text-xs text-slate-400">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right - Journey Cards */}
            <div className="space-y-4 animate-fade-in-up delay-400">
              <h2 className="text-white text-xl font-heading font-bold mb-4">Get Started</h2>
              
              {/* Company Card */}
              <Link to="/register?role=company" className="group block">
                <Card className="border-0 bg-white/10 backdrop-blur-md hover:bg-white/20 transition-all duration-300 overflow-hidden" data-testid="journey-company">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-4">
                      <div className="p-3 bg-blue-500/20 rounded-xl group-hover:bg-blue-500/30 transition-colors">
                        <Building2 className="h-8 w-8 text-blue-400" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-heading text-lg font-bold text-white">I'm a Company</h3>
                        <p className="text-slate-300 text-sm">Seeking funding for my business</p>
                      </div>
                      <ArrowRight className="h-5 w-5 text-white/50 group-hover:text-white group-hover:translate-x-1 transition-all" />
                    </div>
                    <div className="mt-4 flex flex-wrap gap-2">
                      {['AI Analysis', 'Funder Matching', 'Fast Decisions'].map((tag, i) => (
                        <span key={i} className="px-2 py-1 bg-white/10 rounded text-xs text-slate-300">{tag}</span>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </Link>

              {/* Funder Card */}
              <Link to="/register?role=funder" className="group block">
                <Card className="border-0 bg-white/10 backdrop-blur-md hover:bg-white/20 transition-all duration-300 overflow-hidden" data-testid="journey-funder">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-4">
                      <div className="p-3 bg-emerald-500/20 rounded-xl group-hover:bg-emerald-500/30 transition-colors">
                        <Landmark className="h-8 w-8 text-emerald-400" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-heading text-lg font-bold text-white">I'm a Funder</h3>
                        <p className="text-slate-300 text-sm">DFI, Bank, or Fund looking to invest</p>
                      </div>
                      <ArrowRight className="h-5 w-5 text-white/50 group-hover:text-white group-hover:translate-x-1 transition-all" />
                    </div>
                    <div className="mt-4 flex flex-wrap gap-2">
                      {['Qualified Leads', 'Credit Memos', 'Risk Insights'].map((tag, i) => (
                        <span key={i} className="px-2 py-1 bg-white/10 rounded text-xs text-slate-300">{tag}</span>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Partner Logo Carousel */}
      <section className="py-10 bg-slate-900 border-y border-slate-800 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-center text-sm text-slate-400 mb-8">Trusted by leading development finance institutions</p>
          
          {/* Carousel */}
          <div className="relative">
            {/* Gradient Masks */}
            <div className="absolute left-0 top-0 bottom-0 w-20 bg-gradient-to-r from-slate-900 to-transparent z-10" />
            <div className="absolute right-0 top-0 bottom-0 w-20 bg-gradient-to-l from-slate-900 to-transparent z-10" />
            
            {/* Scrolling Container */}
            <div className="flex items-center justify-center overflow-hidden">
              <div className="flex items-center gap-8 animate-scroll">
                {[...PARTNERS, ...PARTNERS].map((partner, i) => (
                  <div 
                    key={i} 
                    className="group flex-shrink-0 relative"
                    data-testid={`partner-logo-${partner.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <div className="flex items-center justify-center h-16 px-6 bg-white rounded-xl hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl cursor-pointer">
                      {partner.logo ? (
                        <img 
                          src={partner.logo} 
                          alt={partner.fullName}
                          className="h-10 max-w-[120px] object-contain"
                        />
                      ) : (
                        <div 
                          className="text-xl font-heading font-black"
                          style={{ color: partner.color }}
                        >
                          {partner.name}
                        </div>
                      )}
                    </div>
                    {/* Hover tooltip */}
                    <div className="absolute -bottom-10 left-1/2 transform -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-20">
                      <div className="bg-slate-800 text-white text-xs font-medium px-3 py-1.5 rounded-lg shadow-lg whitespace-nowrap">
                        {partner.fullName}
                        <div className="absolute -top-1 left-1/2 transform -translate-x-1/2 w-2 h-2 bg-slate-800 rotate-45" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Product Offerings Section */}
      <section id="products" className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 border border-blue-100 rounded-full mb-6">
              <Zap className="h-4 w-4 text-blue-600" />
              <span className="text-sm font-medium text-blue-700">Core Product Offerings</span>
            </div>
            <h2 className="font-heading text-3xl md:text-4xl font-black text-slate-900 mb-4" data-testid="products-title">
              What We Offer
            </h2>
            <p className="text-base md:text-lg text-slate-600 max-w-2xl mx-auto">
              Three powerful solutions to transform how you underwrite, monitor, and stress-test credit in frontier markets.
            </p>
          </div>

          {/* Product Cards */}
          <div className="grid md:grid-cols-3 gap-8">
            {/* Underwriting Support */}
            <div className="group relative" data-testid="product-underwriting">
              <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-blue-400 rounded-2xl transform transition-transform group-hover:scale-[1.02] opacity-0 group-hover:opacity-100 blur-xl" />
              <Card className="relative h-full border-2 border-slate-200 hover:border-blue-400 bg-white transition-all duration-300 overflow-hidden">
                <CardContent className="p-8">
                  <div className="p-4 bg-blue-50 rounded-2xl w-fit mb-6 group-hover:bg-blue-100 transition-colors">
                    <FileSearch className="h-10 w-10 text-blue-600" />
                  </div>
                  <h3 className="font-heading text-2xl font-bold text-slate-900 mb-3">Underwriting Support</h3>
                  <p className="text-slate-600 mb-6">
                    AI-powered credit analysis that generates IC-ready credit memos with risk scoring, governance validation, and actionable recommendations.
                  </p>
                  <ul className="space-y-3 mb-6">
                    {[
                      'Dual AI engine (GPT-5.2 + Claude)',
                      'Instant risk grading (AAA to D)',
                      'Default probability modeling',
                      'Key risks & mitigants identification',
                      'Export to PDF/Word',
                    ].map((item, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
                        <CheckCircle className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                  <Link to="/register?role=funder" className="inline-flex items-center gap-2 text-blue-600 font-medium group-hover:gap-3 transition-all">
                    Get Started <ArrowRight className="h-4 w-4" />
                  </Link>
                </CardContent>
              </Card>
            </div>

            {/* Portfolio Diagnostics */}
            <div className="group relative" data-testid="product-diagnostics">
              <div className="absolute inset-0 bg-gradient-to-r from-emerald-600 to-emerald-400 rounded-2xl transform transition-transform group-hover:scale-[1.02] opacity-0 group-hover:opacity-100 blur-xl" />
              <Card className="relative h-full border-2 border-slate-200 hover:border-emerald-400 bg-white transition-all duration-300 overflow-hidden">
                <CardContent className="p-8">
                  <div className="p-4 bg-emerald-50 rounded-2xl w-fit mb-6 group-hover:bg-emerald-100 transition-colors">
                    <PieChart className="h-10 w-10 text-emerald-600" />
                  </div>
                  <h3 className="font-heading text-2xl font-bold text-slate-900 mb-3">Portfolio Diagnostics</h3>
                  <p className="text-slate-600 mb-6">
                    Comprehensive portfolio health monitoring with real-time risk concentration analysis, sector breakdowns, and early warning indicators.
                  </p>
                  <ul className="space-y-3 mb-6">
                    {[
                      'Risk concentration heatmaps',
                      'Sector & geography exposure',
                      'Watch-list management',
                      'Portfolio quality trends',
                      'Automated alert system',
                    ].map((item, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
                        <CheckCircle className="h-4 w-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                  <Link to="/register?role=funder" className="inline-flex items-center gap-2 text-emerald-600 font-medium group-hover:gap-3 transition-all">
                    Get Started <ArrowRight className="h-4 w-4" />
                  </Link>
                </CardContent>
              </Card>
            </div>

            {/* Stress-testing & Repricing */}
            <div className="group relative" data-testid="product-stress-testing">
              <div className="absolute inset-0 bg-gradient-to-r from-purple-600 to-purple-400 rounded-2xl transform transition-transform group-hover:scale-[1.02] opacity-0 group-hover:opacity-100 blur-xl" />
              <Card className="relative h-full border-2 border-slate-200 hover:border-purple-400 bg-white transition-all duration-300 overflow-hidden">
                <CardContent className="p-8">
                  <div className="p-4 bg-purple-50 rounded-2xl w-fit mb-6 group-hover:bg-purple-100 transition-colors">
                    <RefreshCcw className="h-10 w-10 text-purple-600" />
                  </div>
                  <h3 className="font-heading text-2xl font-bold text-slate-900 mb-3">Stress-testing & Repricing</h3>
                  <p className="text-slate-600 mb-6">
                    Monte Carlo simulations and scenario modeling for portfolio stress-testing with dynamic repricing recommendations.
                  </p>
                  <ul className="space-y-3 mb-6">
                    {[
                      'FX shock scenarios',
                      'Commodity price stress tests',
                      'Interest rate sensitivity',
                      'Climate risk scenarios',
                      'Repricing recommendations',
                    ].map((item, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
                        <CheckCircle className="h-4 w-4 text-purple-500 mt-0.5 flex-shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                  <Link to="/register?role=funder" className="inline-flex items-center gap-2 text-purple-600 font-medium group-hover:gap-3 transition-all">
                    Get Started <ArrowRight className="h-4 w-4" />
                  </Link>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 md:py-28 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-4xl font-black text-slate-900 mb-4" data-testid="features-title">
              The Credit Brain for Frontier Markets
            </h2>
            <p className="text-base md:text-lg text-slate-600 max-w-2xl mx-auto">
              Purpose-built AI that understands the nuances of lending in Africa and emerging markets.
            </p>
          </div>

          {/* Features Bento Grid */}
          <div className="grid md:grid-cols-12 gap-6">
            {/* AI Risk Scoring - Large */}
            <div className="md:col-span-8 group">
              <Card className="h-full border-slate-200 hover:border-blue-300 hover:shadow-lg transition-all" data-testid="feature-ai-risk">
                <CardContent className="p-8">
                  <div className="flex items-start gap-4">
                    <div className="p-4 bg-blue-50 rounded-2xl group-hover:bg-blue-100 transition-colors">
                      <Brain className="h-8 w-8 text-blue-600" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-heading text-xl font-bold text-slate-900 mb-2">AI-Powered Risk Scoring</h3>
                      <p className="text-slate-600 mb-4">
                        Dual AI engine powered by GPT-5.2 for analysis and Claude for governance validation. 
                        Get accurate default predictions even with imperfect data.
                      </p>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="bg-slate-50 rounded-lg p-3">
                          <div className="text-2xl font-bold text-blue-600">85%</div>
                          <div className="text-xs text-slate-500">Prediction Accuracy</div>
                        </div>
                        <div className="bg-slate-50 rounded-lg p-3">
                          <div className="text-2xl font-bold text-emerald-600">2min</div>
                          <div className="text-xs text-slate-500">Average Analysis Time</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* FX Risk - Small */}
            <div className="md:col-span-4 group">
              <Card className="h-full border-slate-200 hover:border-emerald-300 hover:shadow-lg transition-all" data-testid="feature-fx">
                <CardContent className="p-6">
                  <div className="p-3 bg-emerald-50 rounded-xl w-fit mb-4 group-hover:bg-emerald-100 transition-colors">
                    <TrendingUp className="h-6 w-6 text-emerald-600" />
                  </div>
                  <h3 className="font-heading text-lg font-bold text-slate-900 mb-2">FX Risk Modeling</h3>
                  <p className="text-sm text-slate-600">
                    Real-time currency exposure analysis with hedging recommendations for multi-currency facilities.
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Climate Risk - Small */}
            <div className="md:col-span-4 group">
              <Card className="h-full border-slate-200 hover:border-amber-300 hover:shadow-lg transition-all" data-testid="feature-climate">
                <CardContent className="p-6">
                  <div className="p-3 bg-amber-50 rounded-xl w-fit mb-4 group-hover:bg-amber-100 transition-colors">
                    <Globe className="h-6 w-6 text-amber-600" />
                  </div>
                  <h3 className="font-heading text-lg font-bold text-slate-900 mb-2">Climate Risk</h3>
                  <p className="text-sm text-slate-600">
                    Assess physical and transition climate risks impacting borrower operations and value chains.
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Sovereign Risk - Medium */}
            <div className="md:col-span-4 group">
              <Card className="h-full border-slate-200 hover:border-purple-300 hover:shadow-lg transition-all" data-testid="feature-sovereign">
                <CardContent className="p-6">
                  <div className="p-3 bg-purple-50 rounded-xl w-fit mb-4 group-hover:bg-purple-100 transition-colors">
                    <Shield className="h-6 w-6 text-purple-600" />
                  </div>
                  <h3 className="font-heading text-lg font-bold text-slate-900 mb-2">Sovereign Risk</h3>
                  <p className="text-sm text-slate-600">
                    Country-level risk transmission modeling and political risk assessment for frontier markets.
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Stress Testing - Medium */}
            <div className="md:col-span-4 group">
              <Card className="h-full border-slate-200 hover:border-rose-300 hover:shadow-lg transition-all" data-testid="feature-stress">
                <CardContent className="p-6">
                  <div className="p-3 bg-rose-50 rounded-xl w-fit mb-4 group-hover:bg-rose-100 transition-colors">
                    <BarChart3 className="h-6 w-6 text-rose-600" />
                  </div>
                  <h3 className="font-heading text-lg font-bold text-slate-900 mb-2">Stress Testing</h3>
                  <p className="text-sm text-slate-600">
                    Monte Carlo simulations and scenario analysis for portfolio stress testing.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Sectors Section */}
      <section id="sectors" className="py-20 md:py-28 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-black text-slate-900 mb-4" data-testid="sectors-title">
              Built for Every Sector
            </h2>
            <p className="text-base md:text-lg text-slate-600 max-w-2xl mx-auto">
              Specialized risk models for Africa's key economic sectors.
            </p>
          </div>

          <Tabs defaultValue="sme" className="w-full">
            <TabsList className="w-full max-w-2xl mx-auto grid grid-cols-2 md:grid-cols-4 h-auto gap-2 bg-transparent">
              <TabsTrigger value="sme" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white py-3 rounded-lg border border-slate-200" data-testid="sector-tab-sme">
                <Building2 className="h-4 w-4 mr-2" />
                SME
              </TabsTrigger>
              <TabsTrigger value="trade" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white py-3 rounded-lg border border-slate-200" data-testid="sector-tab-trade">
                <Ship className="h-4 w-4 mr-2" />
                Trade
              </TabsTrigger>
              <TabsTrigger value="agri" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white py-3 rounded-lg border border-slate-200" data-testid="sector-tab-agri">
                <Wheat className="h-4 w-4 mr-2" />
                Agri
              </TabsTrigger>
              <TabsTrigger value="project" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white py-3 rounded-lg border border-slate-200" data-testid="sector-tab-project">
                <Landmark className="h-4 w-4 mr-2" />
                Project
              </TabsTrigger>
            </TabsList>

            <TabsContent value="sme" className="mt-8">
              <Card className="border-slate-200 bg-white" data-testid="sector-sme-content">
                <CardContent className="p-8 md:p-12">
                  <div className="grid md:grid-cols-2 gap-8 items-center">
                    <div>
                      <h3 className="font-heading text-2xl font-bold text-slate-900 mb-4">SME Lending</h3>
                      <p className="text-slate-600 mb-6">
                        Underwrite small and medium enterprises with limited financial history using alternative data 
                        signals and AI-powered cash flow analysis.
                      </p>
                      <ul className="space-y-3">
                        {[
                          'Alternative data integration (mobile money, utilities)',
                          'Cash flow pattern recognition',
                          'Sector-specific benchmarking',
                          'Owner/guarantor assessment'
                        ].map((item, i) => (
                          <li key={i} className="flex items-start gap-2 text-slate-600">
                            <CheckCircle className="h-5 w-5 text-emerald-500 mt-0.5 flex-shrink-0" />
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div className="bg-slate-50 rounded-xl p-8 text-center">
                      <Building2 className="h-24 w-24 text-blue-600 mx-auto mb-4" />
                      <div className="text-3xl font-heading font-black text-slate-900">$500K - $5M</div>
                      <div className="text-slate-500">Typical loan size</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="trade" className="mt-8">
              <Card className="border-slate-200 bg-white" data-testid="sector-trade-content">
                <CardContent className="p-8 md:p-12">
                  <div className="grid md:grid-cols-2 gap-8 items-center">
                    <div>
                      <h3 className="font-heading text-2xl font-bold text-slate-900 mb-4">Trade Finance</h3>
                      <p className="text-slate-600 mb-6">
                        De-risk import/export financing with real-time FX analysis, counterparty assessment, 
                        and supply chain visibility across African trade corridors.
                      </p>
                      <ul className="space-y-3">
                        {[
                          'Real-time FX exposure monitoring',
                          'Counterparty and correspondent bank risk',
                          'Supply chain and logistics risk factors',
                          'Letter of credit optimization'
                        ].map((item, i) => (
                          <li key={i} className="flex items-start gap-2 text-slate-600">
                            <CheckCircle className="h-5 w-5 text-emerald-500 mt-0.5 flex-shrink-0" />
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div className="bg-slate-50 rounded-xl p-8 text-center">
                      <Ship className="h-24 w-24 text-blue-600 mx-auto mb-4" />
                      <div className="text-3xl font-heading font-black text-slate-900">$1M - $20M</div>
                      <div className="text-slate-500">Typical facility size</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="agri" className="mt-8">
              <Card className="border-slate-200 bg-white" data-testid="sector-agri-content">
                <CardContent className="p-8 md:p-12">
                  <div className="grid md:grid-cols-2 gap-8 items-center">
                    <div>
                      <h3 className="font-heading text-2xl font-bold text-slate-900 mb-4">Agri-Finance</h3>
                      <p className="text-slate-600 mb-6">
                        Price agricultural lending risk with climate intelligence, commodity price modeling, 
                        and seasonal cash flow analysis specific to African farming cycles.
                      </p>
                      <ul className="space-y-3">
                        {[
                          'Climate and weather impact modeling',
                          'Commodity price risk scenarios',
                          'Seasonal cash flow patterns',
                          'Input cost and yield correlation'
                        ].map((item, i) => (
                          <li key={i} className="flex items-start gap-2 text-slate-600">
                            <CheckCircle className="h-5 w-5 text-emerald-500 mt-0.5 flex-shrink-0" />
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div className="bg-slate-50 rounded-xl p-8 text-center">
                      <Wheat className="h-24 w-24 text-amber-600 mx-auto mb-4" />
                      <div className="text-3xl font-heading font-black text-slate-900">$100K - $10M</div>
                      <div className="text-slate-500">Typical loan size</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="project" className="mt-8">
              <Card className="border-slate-200 bg-white" data-testid="sector-project-content">
                <CardContent className="p-8 md:p-12">
                  <div className="grid md:grid-cols-2 gap-8 items-center">
                    <div>
                      <h3 className="font-heading text-2xl font-bold text-slate-900 mb-4">Project Finance</h3>
                      <p className="text-slate-600 mb-6">
                        Model long-tenor infrastructure credit with sovereign spillover analysis, 
                        construction risk factors, and off-taker creditworthiness assessment.
                      </p>
                      <ul className="space-y-3">
                        {[
                          'Sovereign and political risk integration',
                          'Construction and completion risk',
                          'Off-taker and revenue contract analysis',
                          'Environmental and social risk scoring'
                        ].map((item, i) => (
                          <li key={i} className="flex items-start gap-2 text-slate-600">
                            <CheckCircle className="h-5 w-5 text-emerald-500 mt-0.5 flex-shrink-0" />
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div className="bg-slate-50 rounded-xl p-8 text-center">
                      <Landmark className="h-24 w-24 text-purple-600 mx-auto mb-4" />
                      <div className="text-3xl font-heading font-black text-slate-900">$10M - $500M</div>
                      <div className="text-slate-500">Typical project size</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-4xl font-black text-slate-900 mb-4" data-testid="how-it-works-title">
              How Apaka Works
            </h2>
            <p className="text-base md:text-lg text-slate-600 max-w-2xl mx-auto">
              From data to decision in three steps. Built for speed without sacrificing rigor.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                step: '01',
                icon: Database,
                color: 'blue',
                title: 'Connect Your Data',
                description: 'Upload loan applications or connect via API. Our system ingests structured and unstructured data from any source.'
              },
              {
                step: '02',
                icon: Brain,
                color: 'emerald',
                title: 'AI Risk Analysis',
                description: 'Dual AI engines analyze credit, FX, sovereign, and climate risks. Claude validates for governance and consistency.'
              },
              {
                step: '03',
                icon: LineChart,
                color: 'purple',
                title: 'Actionable Decisions',
                description: 'Get risk scores, recommended pricing, conditions, and explainable insights ready for your credit committee.'
              }
            ].map((item, i) => (
              <div key={i} className="relative" data-testid={`step-${item.step}`}>
                <div className="text-6xl font-heading font-black text-slate-100 absolute -top-4 -left-2">{item.step}</div>
                <div className="relative bg-white rounded-xl p-8 border-2 border-slate-200 hover:border-blue-400 hover:shadow-lg transition-all">
                  <div className={`p-4 bg-${item.color}-50 rounded-xl w-fit mb-6`}>
                    <item.icon className={`h-8 w-8 text-${item.color}-600`} />
                  </div>
                  <h3 className="font-heading text-xl font-bold text-slate-900 mb-3">{item.title}</h3>
                  <p className="text-slate-600">{item.description}</p>
                </div>
                {i < 2 && (
                  <ChevronRight className="hidden md:block absolute top-1/2 -right-4 h-8 w-8 text-slate-300 transform -translate-y-1/2" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Request Demo Section */}
      <section id="demo" className="py-20 md:py-28 bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2" />
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full blur-3xl translate-x-1/2 translate-y-1/2" />
        </div>
        
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="text-white">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full mb-6">
                <Play className="h-4 w-4 text-amber-400" />
                <span className="text-sm font-medium">Interactive Demo</span>
              </div>
              
              <h2 className="font-heading text-3xl md:text-4xl font-black mb-6" data-testid="demo-section-title">
                See Apaka in Action with a Real Analysis
              </h2>
              
              <p className="text-lg text-blue-100 mb-8">
                Experience our AI credit engine with a complete demo analysis of a Nigerian agri-business. 
                See exactly how we help you make faster, smarter lending decisions.
              </p>
              
              <ul className="space-y-4 mb-8">
                {[
                  { icon: FileSearch, text: 'Full company profile & document review' },
                  { icon: Brain, text: 'AI-powered risk scoring with BBB grade' },
                  { icon: BarChart3, text: 'Interactive sensitivity & stress testing' },
                  { icon: Globe, text: 'FX analysis with hedging recommendations' },
                  { icon: LineChart, text: 'Monte Carlo simulations & VaR analysis' },
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3">
                    <div className="p-2 bg-white/10 rounded-lg">
                      <item.icon className="h-5 w-5 text-amber-400" />
                    </div>
                    <span className="text-blue-50">{item.text}</span>
                  </li>
                ))}
              </ul>
              
              <Link to="/register?role=funder&demo=true">
                <Button size="lg" className="bg-white text-blue-700 hover:bg-blue-50 font-bold px-8 h-14 text-lg" data-testid="request-demo-btn">
                  <Eye className="h-5 w-5 mr-2" />
                  Request Demo Access
                </Button>
              </Link>
              
              <p className="text-sm text-blue-200 mt-4">
                Free registration required • No credit card needed
              </p>
            </div>
            
            {/* Demo Preview Card */}
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-amber-400 to-orange-500 rounded-2xl blur-xl opacity-20 animate-pulse" />
              <Card className="relative border-0 shadow-2xl overflow-hidden">
                <div className="bg-slate-900 text-white p-4 flex items-center gap-2">
                  <div className="flex gap-1.5">
                    <div className="w-3 h-3 rounded-full bg-red-500" />
                    <div className="w-3 h-3 rounded-full bg-amber-500" />
                    <div className="w-3 h-3 rounded-full bg-green-500" />
                  </div>
                  <span className="text-sm text-slate-400 ml-2">Demo Analysis Preview</span>
                </div>
                <CardContent className="p-6 bg-white">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <p className="text-sm text-slate-500">Borrower</p>
                      <p className="font-heading font-bold text-slate-900">SunRise Agro Industries</p>
                      <p className="text-sm text-slate-600">Agri-Finance • Nigeria</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-slate-500">Risk Grade</p>
                      <span className="inline-block px-3 py-1 bg-blue-100 text-blue-700 font-bold rounded-full">BBB</span>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3 mb-4">
                    <div className="p-3 bg-slate-50 rounded-lg">
                      <p className="text-xs text-slate-500">Facility</p>
                      <p className="font-bold text-slate-900">$4.5M</p>
                    </div>
                    <div className="p-3 bg-slate-50 rounded-lg">
                      <p className="text-xs text-slate-500">Tenor</p>
                      <p className="font-bold text-slate-900">30 months</p>
                    </div>
                    <div className="p-3 bg-slate-50 rounded-lg">
                      <p className="text-xs text-slate-500">All-in Rate</p>
                      <p className="font-bold text-slate-900">11.5%</p>
                    </div>
                    <div className="p-3 bg-slate-50 rounded-lg">
                      <p className="text-xs text-slate-500">Default Prob.</p>
                      <p className="font-bold text-slate-900">2.8%</p>
                    </div>
                  </div>
                  
                  <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-100">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-5 w-5 text-emerald-600" />
                      <span className="font-medium text-emerald-800">APPROVE WITH CONDITIONS</span>
                    </div>
                    <p className="text-sm text-emerald-700 mt-1">7 documents reviewed • 5 conditions</p>
                  </div>
                  
                  <div className="mt-4 pt-4 border-t border-slate-100">
                    <p className="text-xs text-slate-400 text-center">
                      ↑ This is a preview • Register to explore the full interactive analysis
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-28 bg-slate-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-black text-white mb-6" data-testid="cta-title">
            Ready to Transform Your Credit Decisions?
          </h2>
          <p className="text-lg text-slate-400 mb-12 max-w-2xl mx-auto">
            Join the leading banks, DFIs, and funds using Apaka to unlock capital for frontier markets.
          </p>
          
          <div className="grid sm:grid-cols-2 gap-6 max-w-2xl mx-auto">
            <Link to="/register?role=company">
              <Card className="bg-white border-0 hover:shadow-xl transition-all group cursor-pointer">
                <CardContent className="p-6">
                  <Building2 className="h-8 w-8 text-blue-600 mb-4" />
                  <h3 className="font-heading text-lg font-bold text-slate-900 mb-2">For Companies</h3>
                  <p className="text-sm text-slate-600 mb-4">Apply for funding and get matched with investors</p>
                  <span className="text-blue-600 font-medium flex items-center gap-2 group-hover:gap-3 transition-all">
                    Start Application <ArrowRight className="h-4 w-4" />
                  </span>
                </CardContent>
              </Card>
            </Link>
            <Link to="/register?role=funder">
              <Card className="bg-white border-0 hover:shadow-xl transition-all group cursor-pointer">
                <CardContent className="p-6">
                  <Landmark className="h-8 w-8 text-emerald-600 mb-4" />
                  <h3 className="font-heading text-lg font-bold text-slate-900 mb-2">For Funders</h3>
                  <p className="text-sm text-slate-600 mb-4">Find pre-qualified deals and analyze with AI</p>
                  <span className="text-emerald-600 font-medium flex items-center gap-2 group-hover:gap-3 transition-all">
                    Join as Funder <ArrowRight className="h-4 w-4" />
                  </span>
                </CardContent>
              </Card>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-slate-950 text-slate-400">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <img src={LOGO_URL} alt="Apaka" className="h-10 brightness-0 invert mb-4" />
              <p className="text-sm">
                AI-native credit infrastructure for frontier markets.
              </p>
            </div>
            <div>
              <h4 className="font-heading font-bold text-white mb-4">Platform</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#features" className="hover:text-white">Features</a></li>
                <li><a href="#sectors" className="hover:text-white">Sectors</a></li>
                <li><a href="#how-it-works" className="hover:text-white">How It Works</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-heading font-bold text-white mb-4">Company</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#" className="hover:text-white">About Us</a></li>
                <li><a href="#" className="hover:text-white">Careers</a></li>
                <li><a href="#" className="hover:text-white">Contact</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-heading font-bold text-white mb-4">Legal</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#" className="hover:text-white">Privacy Policy</a></li>
                <li><a href="#" className="hover:text-white">Terms of Service</a></li>
                <li><a href="#" className="hover:text-white">Security</a></li>
              </ul>
            </div>
          </div>
          <div className="mt-12 pt-8 border-t border-slate-800 text-center text-sm">
            <p>© {new Date().getFullYear()} Apaka. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
